import { useEffect, useState } from "react";
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  CalendarPlus,
  Clock3,
  Coffee,
  Moon,
  Palmtree,
  RefreshCw,
  Sun,
  Users,
  WalletCards,
} from "lucide-react";
import { api, ApiError } from "../api";
import type { DashboardData, Handover, SessionPayload, Shift, OrganizationUnit } from "../types";
import {
  dateLabel,
  dateRangeLabel,
  LEAVE_LABELS,
  numberLabel,
  SHIFT_LABELS,
  todayIso,
} from "../utils";
import {
  Alert,
  Button,
  Card,
  Empty,
  ShiftBadge,
  SkeletonCards,
  StatusBadge,
} from "../components/ui";
import { ORGANIZATION_UNIT_LABELS } from "../../shared/domain-types";

const shiftIcons: Record<Shift, typeof Sun> = {
  SABAH: Sun,
  OGLE: Coffee,
  GECE: Moon,
  OFF: Palmtree,
};

interface DashboardAttendance {
  available: number | null;
  total: number;
  rate: number | null;
  label: string | null;
  projectedAvailable: number | null;
  projectedRate: number | null;
  projectedLabel: string | null;
  counts: {
    specialOff: number;
    fullAbsence: number;
    partialLeave: number;
    shiftChanges: number;
    pending: number;
  };
  specialOffPeriods?: Array<{ schedulePattern: string; label?: string; people?: Array<{ id: string | number; displayName: string }> }>;
}

interface OperationsDashboardData {
  date: string;
  managementShift: {
    code: "R1" | "R2" | "DAY_08_17";
    label: string;
    shift: Exclude<Shift, "GECE" | "OFF"> | null;
    hours: string;
  } | null;
  matchedTeams: Array<{
    team: "A" | "B" | "C" | "D";
    shift: Shift;
    timeRange?: string;
    chief: { id: string | number; name: string; title?: string | null } | null;
    principalChief?: { id: string | number; name: string; title?: string | null } | null;
    effectiveChief?: { id: string | number; name: string; title?: string | null } | null;
    isDelegated?: boolean;
    delegationStart?: string | null;
    delegationEnd?: string | null;
    attendance: DashboardAttendance;
    unit?: OrganizationUnit;
    units?: Array<{
      unit: OrganizationUnit;
      chief: { id: string | number; name: string } | null;
      principalChief?: { id: string | number; name: string } | null;
      effectiveChief?: { id: string | number; name: string } | null;
      isDelegated?: boolean; delegationStart?: string | null; delegationEnd?: string | null;
      attendance: DashboardAttendance;
      groups?: { approvedFullAbsences?: Array<{ id: string | number; displayName: string; reason?: string }>; partialLeaves?: Array<{ id: string | number; displayName: string; reason?: string }> };
    }>;
    groups?: {
      approvedFullAbsences?: Array<{ id: string | number; displayName: string; reason?: string }>;
      partialLeaves?: Array<{ id: string | number; displayName: string; reason?: string }>;
    };
  }>;
  supervisors: Array<{
    id: string | number;
    name: string;
    managementShift: "R1" | "R2" | "DAY_08_17";
    shift: Exclude<Shift, "GECE" | "OFF"> | null;
    hours: string;
    matchedTeam?: "A" | "B" | "C" | "D" | null;
  }>;
  personnelMatch?: {
    effectiveShift: Shift;
    team?: "A" | "B" | "C" | "D" | null;
    schedulePattern?: string | null;
    supervisors?: Array<{
      id: string | number;
      name: string;
      managementShift: "R1" | "R2";
      hours: string;
    }>;
  } | null;
  handoffs: Handover[];
}

function delegationDate(value: string) {
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

function delegationRange(start: string | null | undefined, end: string | null | undefined) {
  if (!start || !end) return "";
  return start === end
    ? delegationDate(start)
    : `${delegationDate(start)}–${delegationDate(end)}`;
}

function compactRate(value: number | null): string {
  return value === null
    ? "Ana ekip bugün OFF"
    : `%${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(value)}`;
}

function ManagementShiftCard({
  operations,
  role,
  navigate,
}: {
  operations: OperationsDashboardData;
  role: string;
  navigate: (path: string) => void;
}) {
  const management = operations.managementShift;
  return (
    <div className="dashboard-management-card">
      <div className="dashboard-management-card__title">
        <span className="dashboard-shift-card__icon"><BriefcaseBusiness aria-hidden="true" /></span>
        <div>
          <span>Bugünkü yönetim vardiyanız</span>
          <strong>
            {management
              ? `${management.code === "DAY_08_17" ? "08:00–17:00" : management.code} — ${management.shift ? SHIFT_LABELS[management.shift] : "Sabit yönetim"}`
              : "Yönetim vardiyası atanmamış"}
          </strong>
          {management ? <small>{management.hours}</small> : null}
        </div>
      </div>
      <div className="dashboard-management-card__teams">
        {operations.matchedTeams.flatMap((team) => team.units?.length
          ? team.units.filter((unit) => unit.unit === "COMPOSITE" || unit.unit === "STRUCTURAL").map((unit) => ({ ...team, ...unit }))
          : [team]).map((item) => (
          <article key={`${item.unit ?? "COMPOSITE"}-${item.team}-${item.timeRange ?? item.shift}`}>
            <header>
              <span className={`team-dot team-dot--${item.team.toLowerCase()}`}>{item.team}</span>
              <div>
                <strong>{item.unit ? `${ORGANIZATION_UNIT_LABELS[item.unit]} · ` : ""}{item.team} Ekibi / {SHIFT_LABELS[item.shift]}</strong>
                {item.timeRange ? <small>{item.timeRange}</small> : null}
                <small className="dashboard-chief-line">
                  Başteknisyen: {item.principalChief?.name ?? item.chief?.name ?? "Atanmamış"}
                </small>
                {item.isDelegated && item.effectiveChief ? (
                  <small className="dashboard-chief-delegate">
                    {item.effectiveChief.name} (Vekaleten {delegationRange(item.delegationStart, item.delegationEnd)})
                  </small>
                ) : null}
              </div>
            </header>
            <div className="dashboard-management-card__rates">
              <span><b>{item.attendance.available ?? "—"}/{item.attendance.total}</b> beklenen · {compactRate(item.attendance.rate)} · {item.attendance.label ?? "—"}</span>
              <span>Bekleyenler gerçekleşirse <b>{item.attendance.projectedAvailable ?? "—"}/{item.attendance.total}</b> · {compactRate(item.attendance.projectedRate)}</span>
            </div>
            <div className="dashboard-management-card__counts">
              <span>{item.attendance.counts.specialOff} özel OFF</span>
              <span>{item.attendance.counts.fullAbsence} tam gün yokluk</span>
              <span>{item.attendance.counts.partialLeave} kısmi izin</span>
            </div>
            {item.groups?.approvedFullAbsences?.length ? (
              <small className="dashboard-management-card__absence">
                Tam gün yokluk: {item.groups.approvedFullAbsences.map((person) => `${person.displayName}${person.reason ? ` — ${person.reason}` : ""}`).join(", ")}
              </small>
            ) : null}
            {item.groups?.partialLeaves?.length ? (
              <small className="dashboard-management-card__absence">
                Kısmi izin: {item.groups.partialLeaves.map((person) => person.displayName).join(", ")}
              </small>
            ) : null}
            {item.attendance.specialOffPeriods?.map((period) => (
              <small className="dashboard-management-card__off" key={period.schedulePattern}>
                {period.label ?? `${period.schedulePattern} özel OFF döneminde`}
                {period.people?.length ? ` · ${period.people.length} personel beklenen kadroya dahil edilmedi.` : ""}
              </small>
            ))}
          </article>
        ))}
      </div>
      <button className="text-link" onClick={() => navigate("operations")}>
        {role === "manager" ? "İki ekibin detayını aç" : "Operasyon detayını aç"} <ArrowRight size={15} />
      </button>
    </div>
  );
}

function HandoverCard({
  handovers,
  collapsible = false,
}: {
  handovers: Handover[];
  collapsible?: boolean;
}) {
  const handover = handovers[0];
  if (!handover) return null;
  const warnings = [...new Set(handovers.flatMap((item) => item.warnings ?? []))];
  const routeTime = (route: Handover) => {
    if (route.fromShift === "GECE" && route.toShift === "SABAH") return "07.00";
    if (route.fromShift === "SABAH" && route.toShift === "OGLE") return "15.00";
    return "23.00";
  };
  const dateHeading = (
    <span className="handover__date">
      <Clock3 size={17} />
      <span>
        {handover.date === todayIso()
          ? "Bugün"
          : dateLabel(handover.date, "short")}
      </span>
    </span>
  );
  const content = (
    <div className="handover__content">
      {handovers.map((route) => (
        <div
          className="handover__route"
          key={`${route.fromShift ?? "GECE"}-${route.toShift ?? "SABAH"}`}
        >
          <span className="handover__side">
            <span
              className={`team-dot team-dot--${route.nightTeam.toLowerCase()}`}
            >
              {route.nightTeam}
            </span>
            <span className="handover__person">
              <small>
                {routeTime(route)} · {SHIFT_LABELS[route.fromShift ?? "GECE"]}
              </small>
              <strong>
                {route.fromName ??
                  `${route.nightTeam} ekibinden başka personel`}
              </strong>
            </span>
          </span>
          <ArrowRight aria-label="devreder" />
          <span className="handover__side">
            <span
              className={`team-dot team-dot--${route.morningTeam.toLowerCase()}`}
            >
              {route.morningTeam}
            </span>
            <span className="handover__person">
              <small>{SHIFT_LABELS[route.toShift ?? "SABAH"]}</small>
              <strong>
                {route.toName ??
                  `${route.morningTeam} ekibinden başka personel`}
              </strong>
            </span>
          </span>
        </div>
      ))}
      {warnings.map((warning) => (
        <div className="handover__warning" key={warning}>
          {warning}
        </div>
      ))}
    </div>
  );

  if (collapsible) {
    return (
      <details className="handover handover--forecast">
        <summary>
          {dateHeading}
          <span className="handover__summary-hint">
            {handovers.length} devir
          </span>
        </summary>
        {content}
      </details>
    );
  }

  return (
    <article className="handover handover--today">
      {dateHeading}
      {content}
    </article>
  );
}

function TeamWeekCard({
  week,
  navigate,
}: {
  week: NonNullable<DashboardData["teamWeek"]>;
  navigate: (path: string) => void;
}) {
  const formatter = new Intl.DateTimeFormat("tr-TR", {
    timeZone: "UTC",
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
  return (
    <section className="section-block dashboard-team-week">
      <div className="section-heading">
        <div>
          <span className="eyebrow">BU HAFTA</span>
          <h2>{ORGANIZATION_UNIT_LABELS[week.unit]} · {week.team} Ekibi çalışma takvimi</h2>
        </div>
        <button className="text-link" onClick={() => navigate(`calendar?unit=${week.unit}&team=${week.team}`)}>
          Tam takvim <ArrowRight size={15} />
        </button>
      </div>
      <div className="dashboard-team-week__scroller" tabIndex={0} aria-label={`${ORGANIZATION_UNIT_LABELS[week.unit]} ${week.team} ekibi haftalık çalışma takvimi`}>
        <div className="dashboard-team-week__grid">
          {week.days.map((day) => {
            const isToday = day.date === todayIso();
            const shift = day.personalShift ?? day.teamShift;
            const DayIcon = shiftIcons[shift];
            return (
              <article className={`dashboard-team-week__day${isToday ? " dashboard-team-week__day--today" : ""}`} key={day.date}>
                <header>
                  <time dateTime={day.date}>{formatter.format(new Date(`${day.date}T00:00:00Z`))}</time>
                  {isToday ? <span>Bugün</span> : null}
                </header>
                <div className={`dashboard-team-week__shift dashboard-team-week__shift--${shift.toLowerCase()}`}>
                  <DayIcon aria-hidden="true" />
                  <strong>{SHIFT_LABELS[shift]}</strong>
                </div>
                <small>Kendi vardiyan</small>
                <footer>
                  <span><b>{day.working}/{day.total}</b> ekip hazır</span>
                  {day.unavailable ? <span>{day.unavailable} katılamıyor</span> : <span>Tam katılım</span>}
                </footer>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function DashboardScreen({
  session,
  navigate,
}: {
  session: SessionPayload;
  navigate: (path: string) => void;
}) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [operations, setOperations] = useState<OperationsDashboardData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const readOnlyOperationsRole = session.user.role === "supervisor" || session.user.role === "manager";
      if (readOnlyOperationsRole) {
        setOperations(await api<OperationsDashboardData>(`/api/operations/dashboard?date=${todayIso()}`));
        setData(null);
      } else {
        const dashboard = await api<DashboardData>("/api/dashboard");
        setData(dashboard);
        if (session.user.role === "employee" || session.user.role === "chief") {
          try {
            setOperations(await api<OperationsDashboardData>(`/api/operations/dashboard?date=${todayIso()}`));
          } catch {
            setOperations(null);
          }
        }
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Ana sayfa yüklenemedi.",
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  const readOnlyOperationsRole = session.user.role === "supervisor" || session.user.role === "manager";
  const currentShift = operations?.personnelMatch?.effectiveShift
    ?? data?.todayShift
    ?? operations?.managementShift?.shift
    ?? "OFF";
  const ShiftIcon = shiftIcons[currentShift];
  const annual =
    data?.balances?.annual ??
    data?.balances?.annualLeave ??
    session.balances?.annual ??
    null;
  const excuse =
    data?.balances?.excuse ??
    data?.balances?.excuseLeave ??
    session.balances?.excuse ??
    null;
  const firstName = session.user.displayName.split(" ")[0] ?? session.user.displayName;
  const schedulePattern = session.user.schedulePattern ?? session.user.assignment?.schedulePattern ?? "—";
  const handovers = data?.handovers ?? operations?.handoffs ?? [];
  const handoverGroups = handovers.reduce<Handover[][]>((groups, handover) => {
    const group = groups.find((items) => items[0]?.date === handover.date);
    if (group) group.push(handover);
    else groups.push([handover]);
    return groups;
  }, []);
  const ownTeam = session.user.mainTeam ?? session.user.assignment?.mainTeam;
  const ownTeamOperation = operations?.matchedTeams.find((item) => item.team === ownTeam);
  const matchingSupervisors = operations?.personnelMatch?.supervisors
    ?? operations?.supervisors.filter((item) =>
      currentShift !== "OFF"
      && currentShift !== "GECE"
      && (item.managementShift === "R1" || item.managementShift === "R2")
      && item.shift === currentShift
      && (!item.matchedTeam || item.matchedTeam === ownTeam)
    )
    ?? [];
  const ready = readOnlyOperationsRole ? Boolean(operations) : Boolean(data);

  return (
    <div className="page dashboard-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__pattern" aria-hidden="true" />
        <div className="dashboard-hero__heading">
          <div>
            <span className="dashboard-hero__date">{dateLabel(todayIso())}</span>
            <h1>Merhaba, {firstName}</h1>
            <p>Bugünkü planınız ve ekip akışınız hazır.</p>
          </div>
          <button className="dashboard-refresh" onClick={() => void load()} aria-label="Ana sayfayı yenile">
            <RefreshCw size={20} />
          </button>
        </div>
        {readOnlyOperationsRole ? (
          operations
            ? <ManagementShiftCard operations={operations} role={session.user.role} navigate={navigate} />
            : <div className="dashboard-management-card dashboard-management-card--loading">
                {loading ? "Yönetim vardiyası hesaplanıyor…" : "Yönetim özeti yüklenemedi. Yeniden deneyin."}
              </div>
        ) : <div className={`shift-summary dashboard-shift-card dashboard-shift-card--${currentShift.toLowerCase()}`}>
          <div className="dashboard-shift-card__icon"><ShiftIcon aria-hidden="true" /></div>
          <div className="dashboard-shift-card__content">
            <span>Bugünkü vardiyan</span>
            <strong>{currentShift === "OFF" ? "Bugün OFF" : SHIFT_LABELS[currentShift]}</strong>
            <small>Alt düzen: {schedulePattern}</small>
            {currentShift === "GECE" ? (
              <small>Gece vardiyasında R1/R2 şef eşleşmesi uygulanmaz.</small>
            ) : matchingSupervisors.length ? (
              <small>Şef: {matchingSupervisors.map((item) => `${item.name} (${item.managementShift})`).join(", ")}</small>
            ) : null}
            {(ownTeamOperation?.principalChief ?? ownTeamOperation?.chief) ? (
              <small className="dashboard-chief-line">
                Başteknisyen: {(ownTeamOperation.principalChief ?? ownTeamOperation.chief)?.name}
              </small>
            ) : null}
            {ownTeamOperation?.isDelegated && ownTeamOperation.effectiveChief ? (
              <small className="dashboard-chief-delegate">
                {ownTeamOperation.effectiveChief.name} (Vekaleten {delegationRange(ownTeamOperation.delegationStart, ownTeamOperation.delegationEnd)})
              </small>
            ) : null}
          </div>
          <div className="dashboard-shift-card__next">
            <span>Sonraki vardiya</span>
            <strong>{data?.nextShift ? SHIFT_LABELS[data.nextShift.shift] : "Hesaplanıyor"}</strong>
            {data?.nextShift ? <small>{dateLabel(data.nextShift.date, "short")}</small> : null}
          </div>
          <img src="/brand/logo-icon.svg" alt="" className="dashboard-shift-card__mark" />
        </div>}
      </section>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading ? (
        <SkeletonCards count={4} />
      ) : ready ? (
        <>
          {!readOnlyOperationsRole ? <section className="section-block dashboard-quick-actions">
            <div className="section-heading"><div><span className="eyebrow">HIZLI İŞLEMLER</span><h2>Ne yapmak istersiniz?</h2></div></div>
            <div className="quick-action-card">
              <Button onClick={() => navigate("requests?create=leave")}><CalendarPlus size={20} />İzin Talebi Oluştur</Button>
              {session.user.role !== "supervisor" ? <Button variant="secondary" onClick={() => navigate("requests?create=shift")}><RefreshCw size={20} />Vardiya Değişikliği</Button> : null}
              <Button variant="secondary" onClick={() => navigate("directory")}><Users size={20} />Personel Rehberi</Button>
            </div>
          </section> : null}
          {!readOnlyOperationsRole && data?.teamToday ? <section className="section-block dashboard-team-today">
            <div className="section-heading">
              <div><span className="eyebrow">EKİBİM BUGÜN</span><h2>{data.teamToday.team} Ekibi · Bu vardiyaya katılamayacaklar</h2></div>
              <button className="text-link" onClick={() => navigate(`calendar?team=${data.teamToday?.team}`)}>Gün ayrıntısı <ArrowRight size={15} /></button>
            </div>
            {data.teamToday.unavailable ? <div className="dashboard-team-today__grid dashboard-team-today__grid--single">
              <div>
                <strong>Gelmeyecekler <span>{data.teamToday.unavailable.length}</span></strong>
                {data.teamToday.unavailable.length ? <ul>{data.teamToday.unavailable.map((person) => (
                  <li key={person.userId}><b>{person.displayName}</b><small>{person.kind === "LEAVE" && person.leaveType ? LEAVE_LABELS[person.leaveType] : person.reason}</small></li>
                ))}</ul> : <small>Bu vardiyaya katılamayacak personel yok.</small>}
              </div>
            </div> : null}
          </section> : null}
          {!readOnlyOperationsRole && data?.teamWeek ? <TeamWeekCard week={data.teamWeek} navigate={navigate} /> : null}
          <section className="section-block handover-section">
            <div className="section-heading">
              <div>
                <span className="eyebrow">EKİPLER ARASI</span>
                <h2>Tahmini İş Devri</h2>
              </div>
              <button
                className="text-link"
                onClick={() => navigate("calendar")}
              >
                Takvimde gör <ArrowRight size={15} />
              </button>
            </div>
            {handoverGroups.length ? (
              <div className="handover-grid">
                {handoverGroups.slice(0, 1).map((handovers) => (
                  <HandoverCard handovers={handovers} key={handovers[0]?.date} />
                ))}
              </div>
            ) : (
              <Empty
                icon={<Clock3 />}
                title="Tahmin bulunamadı"
                text="İş devri bilgisi henüz hesaplanamadı."
              />
            )}
            {handoverGroups.length > 1 ? (
              <div className="handover-forecast-list">
                {handoverGroups.slice(1, 4).map((handovers) => (
                  <HandoverCard
                    handovers={handovers}
                    collapsible
                    key={handovers[0]?.date}
                  />
                ))}
              </div>
            ) : null}
          </section>
          {!readOnlyOperationsRole && data ? <section className="section-block balance-section">
            <div className="section-heading">
              <div><span className="eyebrow">İZİN DURUMU</span><h2>İzin Bakiyeleri</h2></div>
              <button className="text-link" onClick={() => navigate("profile")}>Düzenle <ArrowRight size={15} /></button>
            </div>
            <div className="balance-card-grid">
              <Card className="balance-card"><span className="balance-card__icon"><WalletCards /></span><div><strong>{annual === null ? "Bakiye girilmedi" : `${numberLabel(annual)} gün`}</strong><span>Yıllık izin</span></div><span className="balance-card__bar"><i style={{ width: annual === null ? "0%" : `${Math.max(8, Math.min(100, annual * 4))}%` }} /></span></Card>
              <Card className="balance-card balance-card--excuse"><span className="balance-card__icon"><Clock3 /></span><div><strong>{excuse === null ? "Bakiye girilmedi" : `${numberLabel(excuse)} gün`}</strong><span>Mazeret izni</span></div><span className="balance-card__bar"><i style={{ width: excuse === null ? "0%" : `${Math.max(8, Math.min(100, (excuse / 7) * 100))}%` }} /></span></Card>
            </div>
          </section> : null}
          {!readOnlyOperationsRole && data ? <section className="section-block upcoming-section">
            <div className="section-heading">
              <div><span className="eyebrow">YAKINDA</span><h2>Yaklaşan İzinler</h2></div>
              <button className="text-link" onClick={() => navigate("calendar")}>Tümü <ArrowRight size={15} /></button>
            </div>
            {data.upcomingLeaves?.length ? (
              <div className="compact-list">{data.upcomingLeaves.slice(0, 5).map((leave) => (
                <article key={leave.id}><div className="avatar avatar--small">{leave.displayName.slice(0, 1)}</div><div><strong>{leave.displayName}</strong><span>{LEAVE_LABELS[leave.type]} · {dateRangeLabel(leave.startDate, leave.endDate)}</span></div><StatusBadge status={leave.status} /></article>
              ))}</div>
            ) : <Empty icon={<CalendarDays />} title="Yaklaşan izin yok" text="Yakın tarihte görünen bir izin kaydı bulunmuyor." />}
          </section> : null}
          {!readOnlyOperationsRole && data ? <section className="section-block team-section">
              <div className="section-heading">
                <div><span className="eyebrow">BUGÜN</span><h2>Ekip Durumu</h2></div>
              </div>
              {data.teamStatus?.length ? (
                <div className="team-status-list team-status-grid">
                  {data.teamStatus.map((item) => (
                    <button className="team-status" key={item.team} onClick={() => navigate(`calendar?team=${item.team}`)}>
                      <span
                        className={`team-dot team-dot--${item.team.toLowerCase()}`}
                      >
                        {item.team}
                      </span>
                      <div>
                        <strong>{item.team} Ekibi</strong>
                        <ShiftBadge shift={item.shift} />
                      </div>
                      <div className="team-status__numbers">
                        <span>
                          <b>{item.working}</b> çalışan
                        </span>
                        <span>
                          {item.off} OFF · {item.absent} yok
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <Empty
                  icon={<Users />}
                  title="Durum bulunamadı"
                  text="Bugünkü ekip özeti henüz hazır değil."
                />
              )}
          </section> : null}
          {!readOnlyOperationsRole && data ? <section className={`section-block pending-section${(data.pendingCount ?? 0) === 0 ? " pending-section--empty" : ""}`}>
            <div className="pending-card">
              <span className="pending-card__icon"><Users /></span>
              <div><span className="eyebrow">BEKLEYEN İŞLEMLER</span><strong>{data.pendingCount ?? 0}</strong><p>onay veya sonuç bekleyen kayıt</p></div>
              <Button variant="secondary" onClick={() => navigate(data.pendingCount > 0 ? "approvals" : "requests")}>İşlemlere git <ArrowRight size={16} /></Button>
            </div>
          </section> : null}
        </>
      ) : null}
    </div>
  );
}
